import { useSession } from '../Components/Session';
import API from '../Services/API';
import Model from './Model';

const useBalance = (alwaysRefresh = false, include = null) => {
    const {refresh} = useSession();

    const ret = Model({
        alwaysRefresh: alwaysRefresh,
        returnDataKey: 'balance',
        stateKey: ['balance', include],
        persist: true,
        fetchData: () => API.get('/balance', {include}),
        registerAdditionalMethods: (opts) => {
            return {
                changePlan: (plan, subscriptionPeriod) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/balance/changePlan', { plan, subscriptionPeriod }).then(refresh),
                        null,
                        true,
                    );
                },
                purchaseSMSBundle: (smsCount, autoTopUp) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/balance/purchaseSMSBundle', { smsCount, autoTopUp }).then(refresh),
                        null,
                        true,
                    );
                },
                purchaseLeadCredits: (creditsCount) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/balance/purchaseLeadCredits', { creditsCount }).then(refresh),
                        null,
                        true,
                    );
                },
                redeemVoucher: (voucherCode) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/balance/redeemVoucher', { voucherCode }).then(refresh),
                        null,
                        true,
                    );
                },
                changePayoutThreshold: (payoutThreshold) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/balance/changePayoutThreshold', { payoutThreshold }).then(refresh),
                        null,
                        true,
                    );
                },
                requestPayout: (payout, instant) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/balance/requestPayout', {payout, instant}).then(refresh),
                        null,
                        true,
                    );
                },
                topUp: (pence, message) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/balance/topupRetained', {pence, message}).then(refresh),
                        null,
                        true,
                    );
                }
            }
        },
        defaultLoadingError: 'Failed to load account balance.',
    });

    return {
        ...ret,
        instant: ret?.balance?.instant ?? null,
        balance: ret?.balance?.balance ?? ret?.balance ?? null,
        paymentMethod: ret?.balance?.balance?.paymentMethod ?? ret?.balance?.paymentMethod ?? null,
        payments: ret?.balance?.payments ?? null,
        payoutAvailability: ret?.balance?.payoutAvailability ?? null,
    }
}

const useInvoices = (alwaysRefresh = false) => {
    return Model({
        alwaysRefresh: alwaysRefresh,
        returnDataKey: 'invoices',
        stateKey: 'balanceInvoices',
        fetchData: async () => API.get('/balance/invoices'),
        defaultLoadingError: 'Failed to load invoices.'
    });
}

const usePayouts = (alwaysRefresh = false) => {
    return Model({
        alwaysRefresh: alwaysRefresh,
        returnDataKey: 'payouts',
        stateKey: 'payouts',
        fetchData: async () => API.get('/balance/payouts'),
        defaultLoadingError: 'Failed to load payouts.'
    });
}

const useSMSBundles = (alwaysRefresh = false) => {
    return Model({
        alwaysRefresh: alwaysRefresh,
        returnDataKey: 'bundles',
        stateKey: 'smsBundles',
        fetchData: async () => API.get('/balance/SMSBundles'),
        defaultLoadingError: 'Failed to load SMS bundles.'
    });
}

const useLeadBundles = (alwaysRefresh = false) => {
    return Model({
        alwaysRefresh: alwaysRefresh,
        returnDataKey: 'bundles',
        stateKey: 'leadBundles',
        fetchData: async () => API.get('/balance/leadBundles'),
        defaultLoadingError: 'Failed to load lead bundles.'
    });
}

const useVoucherDetails = (voucherCode) => {
    return Model({
        block: !voucherCode,
        alwaysRefresh: false,
        returnDataKey: 'voucher',
        stateKey: ['voucher', voucherCode],
        fetchData: async () => API.get('/balance/describeVoucher', {voucherCode}).catch(() => null),
        defaultLoadingError: 'Failed to load voucher details.'
    });
}

const payoutFees = (pence) => {
    if (pence >= 100000) {
        return 0;
    } else if (pence >= 50000) {
        return 5;
    } else {
        return 10;
    }
}

const subscriptionPeriods = [
    { value: 'Expire', label: 'Expire' },
    { value: 'Monthly', label: 'Monthly' },
    { value: 'Yearly', label: 'Yearly' },
    { value: 'Forever', label: 'Forever' },
];

const statuses = [
    { value: 'Active', label: 'Active' },
    { value: 'Owing', label: 'Owing' },
    { value: 'Suspended', label: 'Suspended' },
    { value: 'Idle', label: 'Idle' },
    { value: 'Paused', label: 'Paused' },
    { value: 'Closed', label: 'Closed' },
];

const sessionFlags = [
    { value: 'X', label: 'Offline', fullLabel: 'Offline Support' },
    { value: 'R', label: 'Redesign', fullLabel: 'Redesign' },
    { value: 'D', label: 'Dark Mode', fullLabel: 'Dark Mode' },
    { value: 'P', label: 'Power Search', fullLabel: 'Power Search' },
];

export {
    useBalance,
    useInvoices,
    useSMSBundles,
    useLeadBundles,
    usePayouts,
    payoutFees,
    useVoucherDetails,
    subscriptionPeriods,
    statuses,
    sessionFlags,
}
